import { Button, Skeleton } from '@radix-ui/themes';
import type { ReactNode, Ref } from 'react';
import { useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ApplicantCard from './ApplicantCard';
import ApplicantCardSkeleton from './ApplicantCardSkeleton';

const SelectorHeader = ({
  headerCategory,
  stats,
  extra,
}: {
  headerCategory?: string;
  stats: {
    leads: number;
    passed: number;
    accepted: number;
    rejected: number;
    notQualified: number;
    hired: number;
    applications: number;
  };
  extra?: ReactNode;
}) => {
  const { t } = useTranslation();
  const getHeaderTitle = (category?: string) => {
    switch (category) {
      case 'newLeads':
        return <span dangerouslySetInnerHTML={{ __html: t('swiper.selector.header.newLeads') }} />;
      case 'acceptedLeads':
        return <span dangerouslySetInnerHTML={{ __html: t('swiper.selector.header.acceptedLeads') }} />;
      case 'notHired':
        return <span dangerouslySetInnerHTML={{ __html: t('swiper.selector.header.notHired') }} />;
      case 'newApplicants':
        return <span dangerouslySetInnerHTML={{ __html: t('swiper.selector.header.newApplicants') }} />;
      case 'hired':
        return <>{t('swiper.selector.header.hired')}</>;
      default:
        return (
          <>
            <Skeleton width="60%" />
            <br />
            <Skeleton width="5rem" />
          </>
        );
    }
  };

  const getHeaderStat = (category?: string) => {
    switch (category) {
      case 'newLeads':
        return stats.leads + stats.passed;

      case 'acceptedLeads':
        return stats.accepted ?? 0;

      case 'notHired': {
        const notHired = stats.rejected + stats.notQualified;
        return notHired ?? 0;
      }

      case 'hired':
        return stats.hired ?? 0;

      case 'newApplicants':
        return stats.applications ?? 0;

      default:
        return <Skeleton width="3rem" />;
    }
  };

  return (
    <div className="selector-header flex-col">
      <div className="flex flex-row items-center">
        <span className="selector-header-count leading-[1.2]">{getHeaderStat(headerCategory)}</span>
        <h4 className="leading-[1.2]">{getHeaderTitle(headerCategory)}</h4>
      </div>
      <div className="py-1">{extra}</div>
    </div>
  );
};

export type ScrollableDivRef = { customScrollTo: (id: string) => void } & Partial<HTMLDivElement>;

const ApplicantSelector = ({
  applicantsData,
  selected,
  selectedCallback,
  fetchMoreFunction,
  loadingMore,
  stats,
  loading,
  category,
  extra,
  ref,
}: {
  applicantsData: {
    applicants: {
      id: string;
      stage: number;
      score: number;
      status: string;
      accepted: boolean;
      createdAt: string;
      candidate: {
        id: string;
        firstName: string;
        lastName: string;
      };
      aiDetection: {
        isAi: boolean;
      };
      job: { threshold: number };
      activityLog: {
        createdAt: string;
        actionType: string;
        actionSubType: string;
        message: string;
      }[];
    }[];
  };
  selected?: string;
  selectedCallback: (props: { type: string; applicationId: string }) => void;
  fetchMoreFunction: () => void;
  loadingMore: boolean;
  stats: {
    leads: number;
    passed: number;
    accepted: number;
    rejected: number;
    notQualified: number;
    hired: number;
    applications: number;
  };
  loading: boolean;
  category?: string;
  extra?: ReactNode;
  ref?: Ref<ScrollableDivRef>;
}) => {
  const { t } = useTranslation();
  const itemRef = useRef<Map<string, HTMLDivElement | null>>(null);

  function getMap() {
    if (!itemRef.current) {
      // Initialize the Map on first usage.
      itemRef.current = new Map();
    }
    return itemRef.current;
  }

  useImperativeHandle(ref, () => ({
    customScrollTo(id) {
      const node = getMap().get(id);
      if (!node) return;

      node.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    },
  }));
  return (
    <div className="applicant-selector">
      <SelectorHeader stats={stats} headerCategory={category} extra={extra} />
      <div className="scroll-list">
        {applicantsData.applicants.map((application) => {
          return (
            <ApplicantCard
              application={application}
              selected={application.id === selected}
              key={application.id}
              ref={(el) => {
                if (el) getMap().set(application.id, el);
                else getMap().delete(application.id);
              }}
              threshold={application.job.threshold}
              selectCallback={() => selectedCallback({ type: 'select', applicationId: application.id })}
            />
          );
        })}
        {loading &&
          applicantsData.applicants.length === 0 &&
          [...Array(5).keys()].map((_, index) => {
            return <ApplicantCardSkeleton key={`random-array-${index}`} />;
          })}
        {stats &&
          stats.applications &&
          applicantsData &&
          applicantsData.applicants.length > 0 &&
          applicantsData.applicants.length < stats.applications && (
            <Button
              size="3"
              variant="soft"
              color="gray"
              className="my-1 mt-4 w-full"
              loading={loadingMore}
              onClick={() => fetchMoreFunction()}
            >
              {t('swiper.selector.loadMoreApplicantsButton', {
                count: stats.applications - applicantsData.applicants.length,
              })}
            </Button>
          )}
      </div>
      <div className="showing-of my-3 w-full">
        {applicantsData && stats && stats.applications
          ? t('swiper.selector.showingCountOf', {
              shown: applicantsData.applicants.length,
              total: stats.applications,
            })
          : applicantsData
            ? t('swiper.selector.showingCount', { shown: applicantsData.applicants.length })
            : null}
      </div>
    </div>
  );
};
export default ApplicantSelector;
